// useEnsureIntents — Phase 9.
//
// Standup passes the visible bucket's conversation ids to the backend, which
// runs the regex classifier on each latest inbound and returns a record per
// id. The frontend renders an intent chip on DecideCard / FollowupRow using
// INTENT_META (a static map of { label, chip class, verbHint }).
//
// Failures are silent — if the backend is down, the chips just don't render.

import { useQuery } from '@tanstack/react-query';
import { apiPost } from '@/lib/api';

export type Intent =
  | 'tracking'
  | 'damage'
  | 'address_change'
  | 'cancel'
  | 'refund'
  | 'proof_change'
  | 'generic';

export type IntentEntry = {
  conversation_id: string;
  intent: Intent;
  confidence: number;
  evidence_phrase: string | null;
  matched_patterns: string[];
  order_number_hint: string | null;
  detected_at: string;
  cached: boolean;
};

export type IntentMeta = Record<
  Intent,
  { label: string; chip: string; verbHint: string }
>;

type IntentsResponse = {
  ok: true;
  intents: Record<string, IntentEntry>;
  meta: IntentMeta;
};

export function useEnsureIntents(ids: string[]) {
  const stableIds = [...ids].sort();
  return useQuery<IntentsResponse>({
    queryKey: ['conversation-intents', stableIds],
    enabled: stableIds.length > 0,
    staleTime: 2 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    queryFn: () =>
      apiPost<IntentsResponse, { conversation_ids: string[] }>(
        '/api/ai/intent/bulk',
        { conversation_ids: stableIds },
      ),
  });
}

// Fallback meta used until the first API response lands. Keeps the chip
// rendering from popping in with stale labels.
export const FALLBACK_INTENT_META: IntentMeta = {
  tracking: { label: "Where's my order?", chip: 'intent-tracking', verbHint: '' },
  damage: { label: 'Damage / defect', chip: 'intent-damage', verbHint: '' },
  address_change: { label: 'Address change', chip: 'intent-address', verbHint: '' },
  cancel: { label: 'Cancel request', chip: 'intent-cancel', verbHint: '' },
  refund: { label: 'Refund ask', chip: 'intent-refund', verbHint: '' },
  proof_change: { label: 'Proof revision', chip: 'intent-proof', verbHint: '' },
  generic: { label: 'General', chip: 'intent-generic', verbHint: '' },
};
